import React from "react";
import {
  DataGridPremium,
  DataGridPremiumProps,
  GRID_AGGREGATION_FUNCTIONS,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import { useDisableHotkeysOnCellSelection } from "hooks/hotkeys";

export const GRID_AGGREGATION_FUNCTIONS_NO_LABEL = {
  ...GRID_AGGREGATION_FUNCTIONS,
  sum: { ...GRID_AGGREGATION_FUNCTIONS.sum, label: "" },
  avg: { ...GRID_AGGREGATION_FUNCTIONS.avg, label: "" },
  min: { ...GRID_AGGREGATION_FUNCTIONS.min, label: "" },
  max: { ...GRID_AGGREGATION_FUNCTIONS.max, label: "" },
  size: { ...GRID_AGGREGATION_FUNCTIONS.size, label: "" },
};

/**
 * Data grid that suspends global hotkeys during cell selection or editing while
 * preserving supplied event handlers.
 */
function HotkeyCompliantDataGridInner<
  TRow extends GridValidRowModel = GridValidRowModel,
>(props: DataGridPremiumProps<TRow> & { ref?: React.Ref<HTMLDivElement> }) {
  const {
    ref,
    onCellSelectionModelChange: existingOnCellSelectionModelChange,
    onCellEditStart: existingOnCellEditStart,
    onCellEditStop: existingOnCellEditStop,
    ...otherProps
  } = props;

  const {
    onCellSelectionModelChange: hotkeyOnCellSelectionModelChange,
    onCellEditStart: hotkeyOnCellEditStart,
    onCellEditStop: hotkeyOnCellEditStop,
  } = useDisableHotkeysOnCellSelection();

  const handleCellSelectionModelChange = React.useCallback(
    (
      ...args: Parameters<
        NonNullable<DataGridPremiumProps["onCellSelectionModelChange"]>
      >
    ) => {
      hotkeyOnCellSelectionModelChange(args[0]);
      existingOnCellSelectionModelChange?.(...args);
    },
    [hotkeyOnCellSelectionModelChange, existingOnCellSelectionModelChange]
  );

  const handleCellEditStart = React.useCallback(
    (
      ...args: Parameters<NonNullable<DataGridPremiumProps["onCellEditStart"]>>
    ) => {
      hotkeyOnCellEditStart();
      existingOnCellEditStart?.(...args);
    },
    [hotkeyOnCellEditStart, existingOnCellEditStart]
  );

  const handleCellEditStop = React.useCallback(
    (
      ...args: Parameters<NonNullable<DataGridPremiumProps["onCellEditStop"]>>
    ) => {
      hotkeyOnCellEditStop();
      existingOnCellEditStop?.(...args);
    },
    [hotkeyOnCellEditStop, existingOnCellEditStop]
  );

  return (
    <DataGridPremium
      ref={ref}
      onCellSelectionModelChange={handleCellSelectionModelChange}
      onCellEditStart={handleCellEditStart}
      onCellEditStop={handleCellEditStop}
      {...otherProps}
    />
  );
}

export const HotkeyCompliantDataGrid = HotkeyCompliantDataGridInner as <
  TRow extends GridValidRowModel = GridValidRowModel,
>(
  props: DataGridPremiumProps<TRow> & { ref?: React.Ref<HTMLDivElement> }
) => React.ReactElement;
